import { useState, ReactNode, useEffect } from "react"
import { Link, usePage, router, Head } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Input } from "../components/ui/input"
import { Button } from "../components/ui/button"
import { Search, Calendar, Clock } from "lucide-react"
import Layout from "../layouts/Layout"
import { BlogPost as BlogPostType, PaginationData } from "../types/blog"
import DataTableResults from "../components/ui/data-table-results"
import DataTablePagination from "../components/ui/data-table-pagination"
import Preloader from "../components/ui/preloader"
import NewsletterSubscription from "../components/NewsletterSubscription"

// Extend the PageProps interface for TypeScript
declare module '@inertiajs/core' {
  interface PageProps {
    blog_posts: BlogPostType[]
    blog_pagination?: PaginationData
    search_params?: Record<string, any>
    available_tags?: string[]
  }
}

const Blog = () => {
  const { blog_posts, blog_pagination, search_params = {}, available_tags = [] } = usePage().props
  // Initialize search state from URL params
  const [searchTerm, setSearchTerm] = useState(search_params.full_text_search_cont || "")
  const [selectedTag, setSelectedTag] = useState(search_params.tags_name_cont || "")
  const [isSearching, setIsSearching] = useState(false)

  // Debounced search function - only trigger when there are actual changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const hasSearchChange = searchTerm !== (search_params.full_text_search_cont || "")
      const hasTagChange = selectedTag !== (search_params.tags_name_cont || "")
      
      if (hasSearchChange || hasTagChange) {
        performSearch()
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchTerm, selectedTag])

  const performSearch = (page?: number) => {
    setIsSearching(true)
    
    const searchParams: any = {}
    
    if (searchTerm.trim()) {
      searchParams.q = { full_text_search_cont: searchTerm.trim() }
    }
    
    if (selectedTag) {
      searchParams.q = { ...searchParams.q, tags_name_cont: selectedTag }
    }

    router.get('/blog', { ...searchParams, page }, {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setIsSearching(false)
    })
  }

  const handleTagClick = (tag: string) => {
    setSelectedTag(selectedTag === tag ? "" : tag)
  }

  const clearSearch = () => {
    setSearchTerm("")
    setSelectedTag("")
    router.get('/blog', {}, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handlePageChange = (page: number) => {
    performSearch(page)
  }

  const featuredPosts = blog_posts.filter(post => post.featured)
  const regularPosts = blog_posts.filter(post => !post.featured)

  return (
    <>
      <Head title="Codeware - Technical Blog" >
        <meta name="description" content="Technical blog about Ruby on Rails development, AI integration, domain registry operations, and indie development." />
      </Head>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Technical Blog</h1>
          <p className="text-xl text-muted-foreground mb-8">
            Insights on Ruby on Rails development, AI integration, domain registry operations, and indie development.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
            {(searchTerm || selectedTag) && (
              <Button variant="outline" onClick={clearSearch}>
                Clear
              </Button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedTag === "" ? "default" : "outline"}
              size="sm"
              onClick={() => handleTagClick("")}
            >
              All Topics
            </Button>
            {available_tags.map((tag) => (
              <Button
                key={tag}
                variant={selectedTag === tag ? "default" : "outline"}
                size="sm"
                onClick={() => handleTagClick(tag)}
              >
                {tag}
              </Button>
            ))}
          </div>
        </div>

        <Preloader isLoading={isSearching}>

          {/* Search Results Info */}
          {blog_pagination && blog_posts.length > 0 && <DataTableResults pagination={blog_pagination} object_name="blog posts" />}

          {/* Featured Posts */}
          {featuredPosts.length > 0 && (
            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-8">Featured Articles</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {featuredPosts.map((post) => (
                  <Card key={post.slug} className="hover:shadow-lg transition-shadow border-primary/20">
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="default" className="bg-primary/10 text-primary border-primary/20">
                          Featured
                        </Badge>
                      </div>
                      <CardTitle className="text-xl line-clamp-2">
                        <Link href={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                          {post.title}
                        </Link>
                      </CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {post.readTime}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {post.tags_string?.split(',').map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* All Posts */}
          {regularPosts.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-8">All Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {regularPosts.map((post) => (
                  <Card key={post.slug} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg line-clamp-2">
                        <Link href={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                          {post.title}
                        </Link>
                      </CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {post.readTime}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {post.tags_string?.split(',').map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {blog_posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No articles found matching your search criteria.</p>
              {(searchTerm || selectedTag) && (
                <Button variant="outline" onClick={clearSearch} className="mt-4">
                  Clear Search
                </Button>
              )}
            </div>
          )}

          {/* Pagination */}
          {blog_pagination && (
            <DataTablePagination 
              pagination={blog_pagination} 
              onPageChange={handlePageChange}
            />
          )}

        </Preloader>

        {/* Newsletter Subscription */}
        <section className="mt-20">
          <div className="max-w-2xl mx-auto">
            <NewsletterSubscription />
          </div>
        </section>
      </div>
    </>
  )
}

Blog.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default Blog